import { useEffect, useMemo, useState } from "react";
import ReactJson from "react-json-view";
import {
    LuShieldCheck,
    LuShieldX,
    LuCheck,
    LuTriangleAlert,
    LuLayoutList,
    LuBraces,
} from "react-icons/lu";

// Renders an "approval required" card emitted by an agent that paused on
// `interrupt(...)`. The operator can edit any field, then approve or
// reject. Once a decision has been submitted, the card collapses to a
// status banner so reload still shows the resolution.
//
// Props:
//   interrupt: {
//     interruptId,
//     payload: { kind, tool, title, description, payload?: <fields>, items?: [] },
//     resolved?: boolean,
//     decision?: { approved: boolean, payload?: object, reason?: string }
//   }
//   disabled: boolean
//   onDecide: ({ interruptId, decision }) => Promise<void>

const labelize = (key) =>
    key
        .replace(/[_-]+/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (c) => c.toUpperCase());

// Used to pick the right input shape. JSON values (object/array) get a
// textarea; primitives stay as <input>.
const isJsonValue = (v) => v !== null && typeof v === "object";

const InterruptWarnings = ({ warnings }) => {
    if (!Array.isArray(warnings) || warnings.length === 0) return null;
    return (
        <div className="agents-interrupt__warnings">
            {warnings.map((w, i) => (
                <div
                    key={w.code ? `${w.code}-${i}` : i}
                    className={`agents-interrupt__warning ${
                        w.severity === "danger" ? "is-danger" : "is-warn"
                    }`}
                >
                    <LuTriangleAlert size="1.05rem" aria-hidden="true" />
                    <div>
                        {w.title && (
                            <div className="agents-interrupt__warning-title">{w.title}</div>
                        )}
                        {w.body && (
                            <div className="agents-interrupt__warning-body">{w.body}</div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

const inferType = (value) => {
    if (isJsonValue(value)) return "json";
    if (typeof value === "boolean") return "boolean";
    if (typeof value === "number") return "number";
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return "date";
    return "text";
};

const formatJson = (v) => {
    try {
        return JSON.stringify(v, null, 2);
    } catch {
        return String(v ?? "");
    }
};

const BATCH_KIND = "batch_approval_required";

const isBatchPayload = (p) =>
    p?.kind === BATCH_KIND && Array.isArray(p.items);

// Build a short, human-friendly digest of a single batch-row payload so
// operators can scan dozens of claims without expanding each one. Falls
// back to a stringified preview if we can't recognize the shape.
const digestBatchItem = (item, idx) => {
    if (!item || typeof item !== "object") {
        return { primary: `Item #${idx + 1}`, secondary: String(item ?? ""), facets: [] };
    }

    const ext = item.externalClaimNumber;
    const member =
        item.memberInformation?.memberName
        ?? [item.memberInformation?.lastName, item.memberInformation?.firstName]
            .filter(Boolean)
            .join(",")
        ?? item.subscriberInformation?.subscriberName;
    const memberId =
        item.memberInformation?.memberIdentificationNumber
        ?? item.insuranceInformation?.subscriberInformation?.identificationNumber;

    const profLine = item.serviceLineItems?.serviceLineItem?.[0];
    const instLine = item.institutionalServiceLineItem?.serviceLine?.[0];
    const line = profLine || instLine || {};
    const dos = line.startDate
        ?? item.statementCoveredPeriod?.startDate
        ?? null;
    const code = line.serviceCode ?? line.revenueCode ?? null;
    const charge =
        item.totalPaymentDue?.chargedAmount
        ?? item.totals
        ?? line.serviceFee
        ?? line.totalChargeAmount
        ?? null;
    const dx =
        item.diagnosisCodes?.diagnosisCode
        ?? item.diagnosisList?.primaryDiagnosisCode
        ?? null;

    return {
        primary: member || ext || `Item #${idx + 1}`,
        secondary: [memberId, ext].filter(Boolean).join(" · "),
        facets: [
            dos && { label: "DOS", value: dos },
            code && { label: "Code", value: code },
            dx && { label: "Dx", value: dx },
            charge && { label: "Charged", value: `$${String(charge).replace(/^\$/, "")}` },
        ].filter(Boolean),
    };
};

const safeJsonParse = (s) => {
    try { return { ok: true, value: JSON.parse(s) }; }
    catch (e) { return { ok: false, error: e.message }; }
};

// Rebuild per-field JSON textareas from a parsed draft object.
const jsonTextFromDraft = (draft, fieldTypes) => {
    const out = {};
    for (const k of Object.keys(draft)) {
        if (fieldTypes[k] === "json") out[k] = formatJson(draft[k]);
    }
    return out;
};

// Collapsible tree viewer — same `react-json-view` setup used on claim /
// benefit-plan screens elsewhere in the app.
//
// IMPORTANT: do NOT pass `sortKeys` here. HRP's SOAP-derived endpoints are
// element-order sensitive (the XSD rejects out-of-order children with
// `cvc-complex-type.2.4.a/d` errors), so the operator must see the JSON in
// the exact order the agent built it — that's the same order we ship to the
// backend.
const PayloadJsonViewer = ({ data, readOnly = false, disabled = false, onChange }) => {
    const src = data && typeof data === "object" ? data : {};
    const canEdit = !readOnly && !disabled && typeof onChange === "function";

    const applyUpdate = (edit) => {
        const updated = edit?.updated_src;
        if (!updated || typeof updated !== "object" || Array.isArray(updated)) return;
        onChange(updated);
    };

    return (
        <div className="agents-interrupt__json-viewer">
            <ReactJson
                src={src}
                name={false}
                collapsed={1}
                collapseStringsAfterLength={120}
                displayDataTypes={false}
                displayObjectSize={false}
                enableClipboard={!disabled}
                sortKeys={false}
                quotesOnKeys={false}
                onEdit={canEdit ? applyUpdate : false}
                onAdd={canEdit ? applyUpdate : false}
                onDelete={canEdit ? applyUpdate : false}
            />
        </div>
    );
};

const PayloadViewToggle = ({ mode, onFields, onRaw, disabled }) => (
    <div
        className="agents-interrupt__view-toggle"
        role="group"
        aria-label="Payload view"
    >
        <button
            type="button"
            className={`agents-interrupt__view-btn ${mode === "fields" ? "is-active" : ""}`}
            onClick={onFields}
            disabled={disabled}
            aria-pressed={mode === "fields"}
        >
            <LuLayoutList size=".9rem" />
            Fields
        </button>
        <button
            type="button"
            className={`agents-interrupt__view-btn ${mode === "raw" ? "is-active" : ""}`}
            onClick={onRaw}
            disabled={disabled}
            aria-pressed={mode === "raw"}
        >
            <LuBraces size=".9rem" />
            Raw JSON
        </button>
    </div>
);

const InterruptCard = ({ interrupt, disabled, onDecide }) => {
    const payload = interrupt?.payload || {};
    const isBatch = isBatchPayload(payload);
    const batchItems = isBatch ? payload.items : [];

    const fields = !isBatch && payload.payload && typeof payload.payload === "object"
        ? payload.payload
        : {};
    const fieldKeys = Object.keys(fields);
    const resolved = !!interrupt.resolved;
    const decisionFromHistory = interrupt.decision || null;

    const fieldTypes = useMemo(() => {
        const out = {};
        for (const k of fieldKeys) out[k] = inferType(fields[k]);
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [interrupt.interruptId]);

    const initialJson = useMemo(() => {
        const out = {};
        for (const k of fieldKeys) {
            if (fieldTypes[k] === "json") out[k] = formatJson(fields[k]);
        }
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [interrupt.interruptId]);

    const [draft, setDraft] = useState(() => ({ ...fields }));
    const [jsonText, setJsonText] = useState(() => ({ ...initialJson }));
    const [jsonErrors, setJsonErrors] = useState({});
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [viewMode, setViewMode] = useState("raw");
    const [resolvedViewMode, setResolvedViewMode] = useState("raw");
    const [batchChecked, setBatchChecked] = useState(() => new Set());

    // Per-row editable copy of each batch item, plus the raw JSON text and
    // any parse errors. Editing happens inside an expanded <details>; the
    // committed `batchDrafts` is what we ship back with the decision.
    const [batchDrafts, setBatchDrafts] = useState(() => batchItems.map((it) => it));
    const [batchJsonText, setBatchJsonText] = useState(() =>
        batchItems.map((it) => formatJson(it)));
    const [batchJsonErrors, setBatchJsonErrors] = useState({});
    const [openRow, setOpenRow] = useState(null);

    useEffect(() => {
        setDraft({ ...fields });
        setJsonText({ ...initialJson });
        setJsonErrors({});
        setReason("");
        setViewMode("raw");
        setResolvedViewMode("raw");
        if (isBatch && batchItems.length) {
            setBatchChecked(new Set(batchItems.map((_, i) => i)));
            setBatchDrafts(batchItems.map((it) => it));
            setBatchJsonText(batchItems.map((it) => formatJson(it)));
            setBatchJsonErrors({});
            setOpenRow(null);
        } else {
            setBatchChecked(new Set());
            setBatchDrafts([]);
            setBatchJsonText([]);
            setBatchJsonErrors({});
            setOpenRow(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [interrupt.interruptId, isBatch, batchItems.length]);

    const toggleBatchRow = (idx) => {
        setBatchChecked((prev) => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx);
            else next.add(idx);
            return next;
        });
    };

    const setAllBatchChecked = (on) => {
        if (!batchItems.length) return;
        if (on) setBatchChecked(new Set(batchItems.map((_, i) => i)));
        else setBatchChecked(new Set());
    };

    const updateBatchRowJsonText = (idx, raw) => {
        setBatchJsonText((prev) => {
            const next = prev.slice();
            next[idx] = raw;
            return next;
        });
        if (raw.trim() === "") {
            setBatchJsonErrors((prev) => {
                const next = { ...prev };
                next[idx] = "Payload cannot be empty";
                return next;
            });
            return;
        }
        const parsed = safeJsonParse(raw);
        if (parsed.ok && parsed.value && typeof parsed.value === "object" && !Array.isArray(parsed.value)) {
            setBatchDrafts((prev) => {
                const next = prev.slice();
                next[idx] = parsed.value;
                return next;
            });
            setBatchJsonErrors((prev) => {
                const next = { ...prev };
                delete next[idx];
                return next;
            });
        } else {
            setBatchJsonErrors((prev) => {
                const next = { ...prev };
                next[idx] = parsed.ok
                    ? "Payload must be a JSON object"
                    : parsed.error;
                return next;
            });
        }
    };

    const updateBatchRowFromTree = (idx, updated) => {
        if (!updated || typeof updated !== "object" || Array.isArray(updated)) return;
        setBatchDrafts((prev) => {
            const next = prev.slice();
            next[idx] = updated;
            return next;
        });
        setBatchJsonText((prev) => {
            const next = prev.slice();
            next[idx] = formatJson(updated);
            return next;
        });
        setBatchJsonErrors((prev) => {
            const next = { ...prev };
            delete next[idx];
            return next;
        });
    };

    const hasBatchErrorsOnSelected = (indices) =>
        indices.some((i) => batchJsonErrors[i]);

    const submitBatch = async (mode) => {
        if (submitting || disabled) return;
        if (mode === "selected" && batchChecked.size === 0) return;
        const indices = mode === "all"
            ? batchItems.map((_, i) => i)
            : [...batchChecked].sort((a, b) => a - b);
        if (hasBatchErrorsOnSelected(indices)) return;
        setSubmitting(true);
        try {
            await onDecide({
                interruptId: interrupt.interruptId,
                decision: {
                    approved: true,
                    payload: {
                        __batchResume: mode === "all"
                            ? { mode: "all" }
                            : { mode: "selected", indices },
                        // Always send the (possibly edited) draft array so the
                        // backend uses the operator's edits instead of the
                        // original model proposal.
                        items: batchDrafts.length === batchItems.length
                            ? batchDrafts
                            : batchItems,
                    },
                },
            });
        } finally {
            setSubmitting(false);
        }
    };

    const applyDraftUpdate = (updated) => {
        setDraft(updated);
        setJsonText(jsonTextFromDraft(updated, fieldTypes));
        setJsonErrors({});
    };

    const updatePrimitive = (key, raw, type) => {
        let coerced = raw;
        if (type === "number") {
            const n = Number(raw);
            coerced = Number.isFinite(n) ? n : raw;
        } else if (type === "boolean") {
            coerced = raw === true || raw === "true";
        }
        setDraft((prev) => ({ ...prev, [key]: coerced }));
    };

    const updateJson = (key, raw) => {
        setJsonText((prev) => ({ ...prev, [key]: raw }));
        if (raw.trim() === "") {
            setDraft((prev) => ({ ...prev, [key]: null }));
            setJsonErrors((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
            return;
        }
        try {
            const parsed = JSON.parse(raw);
            setDraft((prev) => ({ ...prev, [key]: parsed }));
            setJsonErrors((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
        } catch (e) {
            setJsonErrors((prev) => ({ ...prev, [key]: e.message }));
        }
    };

    const switchToFieldsView = () => setViewMode("fields");
    const switchToRawView = () => setViewMode("raw");

    const hasPayloadErrors = Object.keys(jsonErrors).length > 0;
    const selectedBatchCount = batchChecked.size;

    const decide = async (approved) => {
        if (submitting || disabled) return;
        if (approved && hasPayloadErrors) return;
        setSubmitting(true);
        try {
            await onDecide({
                interruptId: interrupt.interruptId,
                decision: {
                    approved,
                    payload: approved ? draft : undefined,
                    reason: approved ? undefined : (reason || null),
                },
            });
        } finally {
            setSubmitting(false);
        }
    };

    const renderFieldsForm = () => (
        <div className="agents-interrupt__form">
            {fieldKeys.map((k) => {
                const type = fieldTypes[k];
                if (type === "json") {
                    const text = jsonText[k] ?? "";
                    const err = jsonErrors[k];
                    return (
                        <label
                            className="agents-interrupt__field is-full agents-interrupt__field--json"
                            key={k}
                        >
                            <span className="agents-interrupt__label">
                                {labelize(k)}
                                <span className="agents-interrupt__hint">JSON</span>
                            </span>
                            <textarea
                                value={text}
                                disabled={submitting || disabled}
                                onChange={(e) => updateJson(k, e.target.value)}
                                spellCheck={false}
                                rows={Math.min(
                                    14,
                                    Math.max(4, text.split("\n").length + 1),
                                )}
                                className={err ? "is-invalid" : ""}
                            />
                            {err && (
                                <span className="agents-interrupt__field-error">
                                    Invalid JSON: {err}
                                </span>
                            )}
                        </label>
                    );
                }

                const v = draft[k];
                const inputType = type === "number"
                    ? "number"
                    : type === "date"
                        ? "date"
                        : "text";
                return (
                    <label className="agents-interrupt__field" key={k}>
                        <span className="agents-interrupt__label">{labelize(k)}</span>
                        <input
                            type={inputType}
                            value={v ?? ""}
                            disabled={submitting || disabled}
                            onChange={(e) => updatePrimitive(k, e.target.value, type)}
                        />
                    </label>
                );
            })}
        </div>
    );

    const renderRawEditor = () => (
        <PayloadJsonViewer
            data={draft}
            disabled={submitting || disabled}
            onChange={applyDraftUpdate}
        />
    );

    const renderFieldsSummary = (data) => (
        <dl className="agents-interrupt__summary">
            {Object.keys(data).map((k) => {
                const v = data[k];
                const isJson = isJsonValue(v);
                return (
                    <div className="agents-interrupt__summary-row" key={k}>
                        <dt>{labelize(k)}</dt>
                        <dd>
                            {isJson ? (
                                <div className="agents-interrupt__json-viewer agents-interrupt__json-viewer--nested">
                                    <PayloadJsonViewer data={v} readOnly />
                                </div>
                            ) : (
                                String(v ?? "")
                            )}
                        </dd>
                    </div>
                );
            })}
        </dl>
    );

    const renderRawPreview = (data) => (
        <PayloadJsonViewer data={data} readOnly />
    );

    if (resolved) {
        const approved = decisionFromHistory?.approved === true;
        const br = decisionFromHistory?.payload?.__batchResume;

        if (isBatch) {
            const total = batchItems.length;
            let detail = "";
            if (approved && br?.mode === "all") detail = `Submitted all ${total} item(s).`;
            else if (approved && br?.mode === "selected" && Array.isArray(br.indices)) {
                detail = `Submitted ${br.indices.length} of ${total} item(s) (selected indices).`;
            } else if (approved) detail = "Batch approved.";
            else detail = "Entire batch declined.";

            return (
                <div className={`agents-msg is-interrupt is-resolved ${approved ? "is-approved" : "is-declined"}`}>
                    <div className="agents-msg__avatar agents-interrupt__avatar">
                        {approved ? <LuShieldCheck size="1rem" /> : <LuShieldX size="1rem" />}
                    </div>
                    <div className="agents-msg__bubble agents-interrupt agents-interrupt--resolved">
                        <div className="agents-interrupt__header">
                            <div className="agents-interrupt__title">
                                {payload.title || "Batch approval"}
                            </div>
                            <span className={`agents-interrupt__badge ${approved ? "is-ok" : "is-bad"}`}>
                                {approved ? <><LuCheck size=".85rem" /> Approved</> : <><LuShieldX size=".85rem" /> Declined</>}
                            </span>
                        </div>
                        <p className="agents-interrupt__batch-summary">{detail}</p>
                        {!approved && decisionFromHistory?.reason && (
                            <div className="agents-interrupt__reason">
                                Reason: {decisionFromHistory.reason}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        const resolvedPayload = decisionFromHistory?.payload ?? fields;
        const resolvedKeys = Object.keys(resolvedPayload);

        return (
            <div className={`agents-msg is-interrupt is-resolved ${approved ? "is-approved" : "is-declined"}`}>
                <div className="agents-msg__avatar agents-interrupt__avatar">
                    {approved ? <LuShieldCheck size="1rem" /> : <LuShieldX size="1rem" />}
                </div>
                <div className="agents-msg__bubble agents-interrupt agents-interrupt--resolved">
                    <div className="agents-interrupt__header">
                        <div className="agents-interrupt__title">
                            {payload.title || "Approval"}
                        </div>
                        <span className={`agents-interrupt__badge ${approved ? "is-ok" : "is-bad"}`}>
                            {approved ? <><LuCheck size=".85rem" /> Approved</> : <><LuShieldX size=".85rem" /> Declined</>}
                        </span>
                    </div>
                    {resolvedKeys.length > 0 && (
                        <>
                            <PayloadViewToggle
                                mode={resolvedViewMode}
                                onFields={() => setResolvedViewMode("fields")}
                                onRaw={() => setResolvedViewMode("raw")}
                                disabled={false}
                            />
                            {resolvedViewMode === "raw"
                                ? renderRawPreview(resolvedPayload)
                                : renderFieldsSummary(resolvedPayload)}
                        </>
                    )}
                    {!approved && decisionFromHistory?.reason && (
                        <div className="agents-interrupt__reason">
                            Reason: {decisionFromHistory.reason}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (isBatch) {
        return (
            <div className="agents-msg is-interrupt">
                <div className="agents-msg__avatar agents-interrupt__avatar">
                    <LuTriangleAlert size="1rem" />
                </div>
                <div className="agents-msg__bubble agents-interrupt agents-interrupt--batch">
                    <div className="agents-interrupt__header">
                        <div className="agents-interrupt__title">
                            {payload.title || "Batch approval required"}
                        </div>
                        {payload.tool && (
                            <span className="agents-interrupt__tool">{payload.tool}</span>
                        )}
                    </div>
                    {payload.description && (
                        <div className="agents-interrupt__desc">{payload.description}</div>
                    )}
                    <InterruptWarnings warnings={payload.warnings} />
                    <p className="agents-interrupt__batch-meta">
                        {batchItems.length} claim{batchItems.length === 1 ? "" : "s"}.
                        {" "}{selectedBatchCount}/{batchItems.length} selected.
                        {" "}Expand a row to review or edit its payload.
                    </p>
                    <div className="agents-interrupt__batch-toolbar">
                        <button
                            type="button"
                            className="agents-interrupt__linkish"
                            onClick={() => setAllBatchChecked(true)}
                            disabled={submitting || disabled}
                        >
                            Select all
                        </button>
                        <span className="agents-interrupt__batch-toolbar-sep">·</span>
                        <button
                            type="button"
                            className="agents-interrupt__linkish"
                            onClick={() => setAllBatchChecked(false)}
                            disabled={submitting || disabled}
                        >
                            Deselect all
                        </button>
                    </div>
                    <ul className="agents-interrupt__batch-list">
                        {batchItems.map((_, idx) => {
                            const row = batchDrafts[idx] ?? batchItems[idx];
                            const digest = digestBatchItem(row, idx);
                            const isOpen = openRow === idx;
                            const rowErr = batchJsonErrors[idx];
                            const checked = batchChecked.has(idx);
                            return (
                                <li
                                    className={`agents-interrupt__batch-item ${rowErr ? "is-invalid" : ""} ${checked ? "" : "is-unchecked"}`}
                                    key={idx}
                                >
                                    <div className="agents-interrupt__batch-row">
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggleBatchRow(idx)}
                                            disabled={submitting || disabled}
                                            aria-label={`Include claim ${idx + 1}`}
                                        />
                                        <span className="agents-interrupt__batch-idx">#{idx + 1}</span>
                                        <button
                                            type="button"
                                            className="agents-interrupt__batch-digest"
                                            onClick={() => setOpenRow(isOpen ? null : idx)}
                                            aria-expanded={isOpen}
                                        >
                                            <span className="agents-interrupt__batch-digest-primary">
                                                {digest.primary}
                                            </span>
                                            {digest.secondary && (
                                                <span className="agents-interrupt__batch-digest-secondary">
                                                    {digest.secondary}
                                                </span>
                                            )}
                                            {digest.facets.length > 0 && (
                                                <span className="agents-interrupt__batch-digest-facets">
                                                    {digest.facets.map((f) => (
                                                        <span
                                                            className="agents-interrupt__batch-facet"
                                                            key={f.label}
                                                        >
                                                            <span className="agents-interrupt__batch-facet-label">
                                                                {f.label}
                                                            </span>
                                                            <span className="agents-interrupt__batch-facet-value">
                                                                {f.value}
                                                            </span>
                                                        </span>
                                                    ))}
                                                </span>
                                            )}
                                            {rowErr && (
                                                <span className="agents-interrupt__batch-row-error">
                                                    JSON error
                                                </span>
                                            )}
                                            <span className="agents-interrupt__batch-toggle">
                                                {isOpen ? "Hide" : "Edit"}
                                            </span>
                                        </button>
                                    </div>
                                    {isOpen && (
                                        <div className="agents-interrupt__batch-editor">
                                            <PayloadJsonViewer
                                                data={row}
                                                disabled={submitting || disabled}
                                                onChange={(updated) => updateBatchRowFromTree(idx, updated)}
                                            />
                                            <details className="agents-interrupt__batch-rawtoggle">
                                                <summary>Edit raw JSON</summary>
                                                <textarea
                                                    value={batchJsonText[idx] ?? ""}
                                                    disabled={submitting || disabled}
                                                    onChange={(e) => updateBatchRowJsonText(idx, e.target.value)}
                                                    spellCheck={false}
                                                    rows={Math.min(
                                                        20,
                                                        Math.max(6, (batchJsonText[idx] ?? "").split("\n").length + 1),
                                                    )}
                                                    className={rowErr ? "is-invalid" : ""}
                                                />
                                                {rowErr && (
                                                    <span className="agents-interrupt__field-error">
                                                        {rowErr}
                                                    </span>
                                                )}
                                            </details>
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>

                    <label className="agents-interrupt__field is-full">
                        <span className="agents-interrupt__label">Reason if declining entire batch (optional)</span>
                        <input
                            type="text"
                            value={reason}
                            placeholder="Why are you rejecting this batch?"
                            disabled={submitting || disabled}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </label>

                    <div className="agents-interrupt__actions agents-interrupt__actions--batch">
                        <button
                            type="button"
                            className="agents-interrupt__btn is-decline"
                            onClick={() => decide(false)}
                            disabled={submitting || disabled}
                        >
                            <LuShieldX size=".95rem" />
                            Decline all
                        </button>
                        <button
                            type="button"
                            className="agents-interrupt__btn is-secondary"
                            onClick={() => submitBatch("selected")}
                            disabled={
                                submitting
                                || disabled
                                || selectedBatchCount === 0
                                || hasBatchErrorsOnSelected([...batchChecked])
                            }
                            title={selectedBatchCount === 0 ? "Select at least one row" : undefined}
                        >
                            <LuCheck size=".95rem" />
                            {submitting ? "Submitting…" : `Approve selected (${selectedBatchCount})`}
                        </button>
                        <button
                            type="button"
                            className="agents-interrupt__btn is-approve"
                            onClick={() => submitBatch("all")}
                            disabled={
                                submitting
                                || disabled
                                || hasBatchErrorsOnSelected(batchItems.map((_, i) => i))
                            }
                            title={
                                hasBatchErrorsOnSelected(batchItems.map((_, i) => i))
                                    ? "Fix JSON errors before approving"
                                    : undefined
                            }
                        >
                            <LuShieldCheck size=".95rem" />
                            Approve all
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="agents-msg is-interrupt">
            <div className="agents-msg__avatar agents-interrupt__avatar">
                <LuTriangleAlert size="1rem" />
            </div>
            <div className="agents-msg__bubble agents-interrupt">
                <div className="agents-interrupt__header">
                    <div className="agents-interrupt__title">
                        {payload.title || "Approval required"}
                    </div>
                    {payload.tool && (
                        <span className="agents-interrupt__tool">
                            {payload.tool}
                        </span>
                    )}
                </div>
                {payload.description && (
                    <div className="agents-interrupt__desc">{payload.description}</div>
                )}
                <InterruptWarnings warnings={payload.warnings} />

                {fieldKeys.length > 0 && (
                    <>
                        <PayloadViewToggle
                            mode={viewMode}
                            onFields={switchToFieldsView}
                            onRaw={switchToRawView}
                            disabled={submitting || disabled}
                        />
                        {viewMode === "raw" ? renderRawEditor() : renderFieldsForm()}
                    </>
                )}

                <label className="agents-interrupt__field is-full">
                    <span className="agents-interrupt__label">Reason if declining (optional)</span>
                    <input
                        type="text"
                        value={reason}
                        placeholder="Why are you rejecting this?"
                        disabled={submitting || disabled}
                        onChange={(e) => setReason(e.target.value)}
                    />
                </label>

                <div className="agents-interrupt__actions">
                    <button
                        type="button"
                        className="agents-interrupt__btn is-decline"
                        onClick={() => decide(false)}
                        disabled={submitting || disabled}
                    >
                        <LuShieldX size=".95rem" />
                        Decline
                    </button>
                    <button
                        type="button"
                        className="agents-interrupt__btn is-approve"
                        onClick={() => decide(true)}
                        disabled={submitting || disabled || hasPayloadErrors}
                        title={hasPayloadErrors ? "Fix the JSON errors before approving" : undefined}
                    >
                        <LuShieldCheck size=".95rem" />
                        {submitting ? "Submitting…" : "Approve"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InterruptCard;
